import React, { ReactNode, useCallback } from 'react';
import { Select, SeverityItem } from '@fabric-ui/components';
import { Box } from '@mui/material';
import { getFieldTypeInfo } from '../../../utils/filterUtils';
import { IconVariant, ItemWithLogo } from '../../ItemWithLogo';
import { useExpressionBuilder } from '../ExpressionBuilderContext';

interface FilterByListProps {
  label: ReactNode;
  isLoadingOptions: boolean;
  options: any[];
  value: any[];
  onChange: (field: any, val: any) => void;
  field: string;
  type: string;
  onFilterChange: ((filter: any) => void) | undefined;
  totalOptionsCount: number;
  isSchemaField: boolean;
  setSearch: (search: string) => void;
}

export const FilterByList: React.FC<FilterByListProps> = ({
  label,
  isLoadingOptions,
  options,
  value,
  onChange,
  field,
  type,
  onFilterChange,
  totalOptionsCount,
  isSchemaField,
  setSearch,
}) => {
  const { excludeNullFromList } = useExpressionBuilder();

  const { isTextField } = getFieldTypeInfo(type);
  const isSeverity = field.split('.')[1] === 'severity';

  const handleChange = useCallback(
    (val: any[]) => {
      onChange([field], val);
      if (type === 'string') {
        const hasNull = val.includes(null);
        const nonNullValues = val.filter((v: any) => v !== null);
        onFilterChange?.({
          expression: {
            fieldName: field,
            stringCondition: val.length > 0 ? { in: { values: nonNullValues }, ...(hasNull && { addNullFilter: true }) } : { contains: '' },
          },
        });
      }
    },
    [field, onChange, onFilterChange, type]
  );

  return (
    <Box className="mt-2 block px-2 pb-2">
      <Select
        showOnlyAutoComplete
        size="small"
        acceptNullValue={!excludeNullFromList}
        label={label as React.ReactNode}
        variant="filter"
        loading={isLoadingOptions}
        {...(isSeverity && { renderOption: ({ value }: any) => <SeverityItem value={value} /> })}
        isMultiple
        showInput={isTextField}
        options={options}
        value={value}
        onChange={handleChange}
        totalOptionsCount={totalOptionsCount}
        renderOption={
          field.endsWith('.source_names')
            ? ({ value }: any) => <ItemWithLogo variant={IconVariant.sourcesMapByName} type={value} textStyle={{ overflow: 'auto' }} />
            : undefined
        }
        filterFunc={
          isSchemaField
            ? (options, { inputValue }) => {
                setSearch(inputValue);
                return options;
              }
            : undefined
        }
      />
    </Box>
  );
};
